import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import {
	json,
	type DataFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { AuthenticityTokenInput } from 'remix-utils/csrf/react'
import { getClientIPAddress } from "remix-utils/get-client-ip-address";
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field, MobileNoCheckboxField, MobileNoField } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireAnonymous} from '#app/utils/auth.server.ts'
//import { validateCSRF } from '#app/utils/csrf.server.ts'
//import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { logger } from '#app/utils/logger'
import { useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server'
import { MobileNoSchema } from '#app/utils/user-validation.ts'
import { prepareOtpVerification, prepareVerification } from './_authlayout.verify'

const AuthSchema = z.object({
	mobileNo: MobileNoSchema,
	isPolicyChecked: z.boolean(),
	redirectTo: z.string().optional()
})

export async function loader({ request }: DataFunctionArgs) {
	await requireAnonymous(request)
	logger.info({
		message: `/auth,user/null-${getClientIPAddress(request.headers)}`,
		labels: { origin: 'user_page' },
	})
	return json({})
}

export async function action({ request }: DataFunctionArgs) {
	const formData = await request.formData()
	// await validateCSRF(formData, request.headers)
	// checkHoneypot(formData)

	const submission = parse(formData, {
		schema: AuthSchema
	})

	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}
	if (!submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	const { mobileNo, redirectTo: fdRedirectTo } = submission.value

	//const response = await sendMsg91OTP(mobileNo)

	if(process.env.OTP_ENABLE === 'TRUE'){
	  const { redirectTo, otpResult: response } = await prepareOtpVerification({ request, type: 'onboarding', target: mobileNo, fdRedirectTo })

        if (response.status === 'success') {
			// return redirect(redirectTo.toString())
			return redirectWithToast(
				redirectTo.toString(),
				{
					type: 'success',
					description: 'Successfully send OTP.'
				}
			)
		} else {
			submission.error[''] = ['Something Wrong, try again later.']
			return json({ status: 'error', submission} as const, { status: 500 })
		}
	}else{
		const { redirectTo } = await prepareVerification({
			period: 10 * 60,
			request,
			type: 'onboarding',
			target: mobileNo,
			fdRedirectTo
		})

		const response = {
			status: 'success',
			msg: 'otp sent successfully'
		}	

		if (response.status === 'success') {
		// return redirect(redirectTo.toString())
		return redirectWithToast(
			redirectTo.toString(),
			{
				type: 'success',
				description: 'Successfully send OTP.'
			}
			)
		} else {
			submission.error[''] = [response?.msg ?? 'Something Wrong, try again later.']
			return json({ status: 'error', submission} as const, { status: 500 })
		}
	}


	// const { redirectTo } = await prepareVerification({
	// 	period: 10 * 60,
	// 	request,
	// 	type: 'onboarding',
	// 	target: mobileNo,
	// })

	// const response = {
	// 	status: 'success',
	// 	msg: 'otp sent successfully'
	// }

	// SEND OTP WITH OTP PROVIDER

	// GET THE OTP 

	// SEND VERIFY OTP

	// IF SUCCESS THEN SAVE MOBILE NO INTO DB ELSE NOT

	// const { otp, ...verificationConfig } = generateTOTP({
	// 	algorithm: 'SHA256',
	// 	period: 10 * 60, // valid for 10 minutes
	// })
	// const type = 'onboarding'
	// const redirectToUrl = new URL(`${getDomainUrl(request)}/verify`)
	// redirectToUrl.searchParams.set(typeQueryParam, type)
	// redirectToUrl.searchParams.set(targetQueryParam, email)
	// const verifyUrl = new URL(redirectToUrl)
	// verifyUrl.searchParams.set(codeQueryParam, otp)

	// const user = await prisma.testUser.create({
	// 	select: { id: true },
	// 	data: {
	// 		mobileNo
	// 	}
	// })
    // SET AUTH COOKIE SESSION WITH USERID
	// if (response.status === 'success') {
	// 	// return redirect(redirectTo.toString())
	// 	return redirectWithToast(
	// 		 redirectTo.toString(),
	// 		{
	// 			type: 'success',
	// 			description: 'Successfully send OTP.'
	// 		}
	// 	)
	// } else {
	// 	submission.error[''] = [response?.msg ?? 'Something Wrong, try again later.']
	// 	return json({ status: 'error', submission} as const, { status: 500 })
	// }

}

export const meta: MetaFunction = () => {
	return [{ title: 'Auth | SuperFD' }]
}

export default function AuthRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()

	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'auth-form',
		constraint: getFieldsetConstraint(AuthSchema),
		defaultValue: { redirectTo, isPolicyChecked: 'true' },
		lastSubmission: actionData?.submission,
		onValidate({ formData }) {
			const result = parse(formData, { schema: AuthSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container flex flex-col justify-center pb-32 pt-20 px-0">
	  
			<div className="text-center">
				<h1 className="md:text-h1 text-h3">Let's upgrade your FDs</h1>
				<p className="mt-3 text-body-sm md:text-body-md text-muted-foreground">
					Please enter your mobile number
				</p>
			</div>
			<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
				<Form method="POST" {...form.props}>
					<AuthenticityTokenInput />
					<HoneypotInputs />
					<MobileNoField
						labelProps={{
							htmlFor: fields.mobileNo.id,
							children: 'Mobile No.',
						}}
						inputProps={{
							...conform.input(fields.mobileNo),
							autoFocus: true,
							autoComplete: 'mobile_no',
							maxLength: 10
						}}
						errors={fields.mobileNo.errors}
					/>

					<MobileNoCheckboxField
					    className='py-2'
						labelProps={{
							htmlFor: fields.isPolicyChecked.id,
							children: <span>By continuing, you agree to our <Link to="/privacy-policy" className="text-[#3b82f6]">Privacy Policy</Link></span>,
						}}
						buttonProps={conform.input(fields.isPolicyChecked, {
							type: 'checkbox',
						})}
						errors={fields.isPolicyChecked.errors}
				
					/>
					<input {...conform.input(fields.redirectTo, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />

					<StatusButton
						className="w-full mt-4"
						status={isPending ? 'pending' : actionData?.status ?? 'idle'}
						type="submit"
						disabled={isPending}
					>
						Send OTP
					</StatusButton>
				</Form>
			</div>

          {/* <div className="mx-auto py-2 flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
            <p className="px-8 text-center text-sm text-muted-foreground">
              By clicking continue, you agree to our{" "}
              <Link
                to="/terms"
                className="underline underline-offset-4 hover:text-primary"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                className="underline underline-offset-4 hover:text-primary"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div> */}
    </div>
	)

	return (
		<div className="container flex flex-col justify-center pb-32 pt-20">
			<div className="text-center">
				<h1 className="text-h1">Let's start your journey!</h1>
				<p className="mt-3 text-body-md text-muted-foreground">
					Please enter your Mobile No.
				</p>
			</div>
			<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
				<Form method="POST" {...form.props}>
					<AuthenticityTokenInput />
					<HoneypotInputs />
					<Field
						labelProps={{
							htmlFor: fields.mobileNo.id,
							children: 'Mobile No.',
						}}
						inputProps={{
							...conform.input(fields.mobileNo),
							autoFocus: true,
							autoComplete: 'mobile_no',
							maxLength: 10
						}}
						errors={fields.mobileNo.errors}
					/>
					<input {...conform.input(fields.redirectTo, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />

					<StatusButton
						className="w-full"
						status={isPending ? 'pending' : actionData?.status ?? 'idle'}
						type="submit"
						disabled={isPending}
					>
						Send OTP
					</StatusButton>
				</Form>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
